import { Filter, Matrix, Point } from 'pixi.js';

const vertex = `
  attribute vec2 aVertexPosition;

  uniform mat3 projectionMatrix;
  uniform mat3 filterMatrix;

  varying vec2 vTextureCoord;
  varying vec2 vFilterCoord;

  uniform vec4 inputSize;
  uniform vec4 outputFrame;

  vec4 filterVertexPosition( void ){
    vec2 position = aVertexPosition * max(outputFrame.zw, vec2(0.)) + outputFrame.xy;
    return vec4((projectionMatrix * vec3(position, 1.0)).xy, 0.0, 1.0);
  }

  vec2 filterTextureCoord( void ){
    return aVertexPosition * (outputFrame.zw * inputSize.zw);
  }

  void main(void) {
    gl_Position = filterVertexPosition();
    vTextureCoord = filterTextureCoord();
    vFilterCoord = ( filterMatrix * vec3( vTextureCoord, 1.0)  ).xy;
  }
`;

const fragment = `
  varying vec2 vFilterCoord;
  varying vec2 vTextureCoord;

  uniform sampler2D uSampler;
  uniform sampler2D mapSampler;
  uniform vec4 filterArea;
  uniform vec2 scale;
  uniform mat2 rotation;

  uniform float uRedScale;
  uniform float uGreenScale;
  uniform float uBlueScale;

  uniform highp vec4 inputSize;
  uniform vec4 inputClamp;

  void main(void) {
    vec4 noise = texture2D(mapSampler, vFilterCoord);

    noise -= 0.5;
    noise.xy = scale * inputSize.zw * (rotation * noise.xy);
    vec2 coords = clamp(vec2(vTextureCoord.x, vTextureCoord.y), inputClamp.xy, inputClamp.zw);

    float sampleR = texture2D(uSampler, coords + (uRedScale * noise.r)).r;
    float sampleG = texture2D(uSampler, coords + (uGreenScale * noise.g)).g;
    float sampleB = texture2D(uSampler, coords + (uBlueScale * noise.b)).b;
    float sampleA = texture2D(uSampler, coords).a;
    gl_FragColor = vec4(sampleR, sampleG, sampleB, sampleA);
    // gl_FragColor = vec4(1.0, 1.0, 1.0, 1.0);
  }
`;

class SplitFilter extends Filter {
  constructor(sprite, redScale, greenScale, blueScale) {
    const maskMatrix = new Matrix();

    sprite.renderable = false; // eslint-disable-line

    super(vertex, fragment, {
      mapSampler: sprite._texture, // eslint-disable-line
      filterMatrix: maskMatrix,
      scale: new Point(1, 1),
      rotation: new Float32Array([1, 0, 0, 1]),
    });
    this.maskSprite = sprite;
    this.maskMatrix = maskMatrix;

    this.scales = {
      red: redScale,
      green: greenScale,
      blue: blueScale,
    };
  }

  apply(filterManager, input, output, clear) {
    this.uniforms.filterMatrix = filterManager.calculateSpriteMatrix(this.maskMatrix, this.maskSprite);
    this.uniforms.uRedScale = this.scales.red;
    this.uniforms.uGreenScale = this.scales.green;
    this.uniforms.uBlueScale = this.scales.blue;

    const wt = this.maskSprite.transform.worldTransform;
    const lenX = Math.sqrt((wt.a * wt.a) + (wt.b * wt.b));
    const lenY = Math.sqrt((wt.c * wt.c) + (wt.d * wt.d));

    if (lenX !== 0 && lenY !== 0) {
      this.uniforms.rotation[0] = wt.a / lenX;
      this.uniforms.rotation[1] = wt.b / lenX;
      this.uniforms.rotation[2] = wt.c / lenY;
      this.uniforms.rotation[3] = wt.d / lenY;
    }

    filterManager.applyFilter(this, input, output, clear);
  }

  get map() {
    return this.uniforms.mapSampler;
  }

  set map(value) {
    this.uniforms.mapSampler = value;
  }
}

export default SplitFilter;
